<script setup>
import { ref, watch, onMounted, nextTick } from "vue";
import Hls from "hls.js";

const props = defineProps({
  videourl: String,
  isMuted: {
    type: Boolean,
    default: true,
  },
});

const videoElement = ref(null);
let hls = null;
let triedWithoutProtocol = false;

const loadVideo = (url) => {
  console.log(`Attempting to load video from URL: ${url}`);
  if (hls) {
    hls.destroy();
  }
  if (Hls.isSupported()) {
    hls = new Hls();
    hls.attachMedia(videoElement.value);
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(url);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setupMediaSession();
        videoElement.value.play().catch((error) => {
          console.log(`Failed to play video: ${error}`);
        });
      });
    });
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.fatal || (data.response && data.response.code === 500)) {
        if (!triedWithoutProtocol && url.includes("?protocol=llhls")) {
          triedWithoutProtocol = true;
          const newUrl = url.replace("?protocol=llhls", "");
          console.log(`Retrying with URL: ${newUrl}`);
          loadVideo(newUrl);
        }
      }
    });
  } else if (videoElement.value.canPlayType("application/vnd.apple.mpegurl")) {
    videoElement.value.src = url;
    videoElement.value.addEventListener("error", () => {
      if (!triedWithoutProtocol && url.includes("?protocol=llhls")) {
        triedWithoutProtocol = true;
        const newUrl = url.replace("?protocol=llhls", "");
        console.log(`Retrying with URL: ${newUrl}`);
        videoElement.value.src = newUrl;
      }
    });
    videoElement.value.addEventListener("loadedmetadata", () => {
      setupMediaSession();
      videoElement.value.play().catch((error) => {
        console.log(`Failed to play video: ${error}`);
      });
    });
  }
};

// Media Session APIを設定してiPhoneでのバックグラウンド再生を有効化
const setupMediaSession = () => {
  if ("mediaSession" in navigator && videoElement.value) {
    const video = videoElement.value;
    
    // メディアメタデータを設定
    navigator.mediaSession.metadata = new MediaMetadata({
      title: "ライブ配信",
      artist: "配信中",
      artwork: [
        // 必要に応じてアートワークを追加
        // { src: '/img/icon.png', sizes: '96x96', type: 'image/png' },
      ],
    });

    // メディアアクションのハンドラーを設定
    navigator.mediaSession.setActionHandler("play", () => {
      video.play().catch((error) => {
        console.error("Failed to play video:", error);
      });
    });

    navigator.mediaSession.setActionHandler("pause", () => {
      video.pause();
    });

    // 再生状態を更新
    const updatePlaybackState = () => {
      if (video.paused) {
        navigator.mediaSession.playbackState = "paused";
      } else {
        navigator.mediaSession.playbackState = "playing";
      }
    };

    // 再生状態の変更を監視
    video.addEventListener("play", updatePlaybackState);
    video.addEventListener("pause", updatePlaybackState);
  }
};

const onLoadedMetadata = () => {
  setupMediaSession();
};

const onPlay = () => {
  if ("mediaSession" in navigator) {
    navigator.mediaSession.playbackState = "playing";
  }
};

const onPause = () => {
  if ("mediaSession" in navigator) {
    navigator.mediaSession.playbackState = "paused";
  }
};

watch(
  () => props.videourl,
  (newUrl) => {
    if (newUrl) {
      loadVideo(newUrl);
    }
  }
);

onMounted(() => {
  loadVideo(props.videourl);
  
  // iOSでのバックグラウンド再生を有効化するための追加設定
  nextTick(() => {
    if (videoElement.value) {
      // iOS Safariでのバックグラウンド再生を許可
      videoElement.value.setAttribute("playsinline", "true");
      videoElement.value.setAttribute("webkit-playsinline", "true");
    }
  });
});
</script>

<template>
  <div class="movie">
    <div class="movie_inner">
      <video
        ref="videoElement"
        :autoplay="true"
        :muted="isMuted"
        :loop="true"
        playsinline
        webkit-playsinline
        x5-playsinline
        height="100%"
        @loadedmetadata="onLoadedMetadata"
        @play="onPlay"
        @pause="onPause"
      ></video>
    </div>
  </div>
</template>

<style scoped>
.movie {
  width: 100%;
  height: 100%;
}
.movie .movie_inner {
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.vjs_video_3-dimensions {
  width: 100%;
  height: 100%;
}
.movie .movie_inner video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
@media (min-width: 960px) {
  .movie {
    width: auto;
    height: 80vh;
  }
  .movie .movie_inner {
    width: auto;
    height: 80vh;
    overflow: hidden;
  }
  .vjs_video_3-dimensions {
    width: auto;
    height: 80vh;
  }
  .movie .movie_inner video {
    position: initial;
    width: auto;
    height: 80vh;
    object-fit: cover;
  }
}
</style>
